// src/pages/Books.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  XMarkIcon,
  BookOpenIcon,
  PencilSquareIcon,
  ShieldExclamationIcon,
  ScaleIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { companyRoute } from "../utils/routeHelpers";

export default function Books() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const cardBase =
    "flex flex-col items-center justify-center " +
    "min-h-[140px] sm:min-h-[180px] " +
    "bg-white border border-gray-200 rounded-2xl shadow-sm " +
    "px-4 py-6 sm:px-6 sm:py-8 " +
    "hover:shadow-md hover:border-gray-300 " +
    "transition focus:outline-none focus:ring-2 focus:ring-appleblueD";

  const Title = ({ children }: { children: React.ReactNode }) => (
    <div className="text-[13px] font-medium text-gray-700 text-center leading-tight">
      {children}
    </div>
  );

  const Subtitle = ({ children }: { children: React.ReactNode }) => (
    <div className="text-xs text-gray-500 mt-1 text-center">{children}</div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center pt-4">
      {/* Header (Apple standard) */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center border-b border-gray-100 px-2 pb-2 mb-5">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD"
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <div className="flex-1 flex justify-center pointer-events-none relative">
          <h2 className="text-[15px] font-semibold text-gray-700 text-center">
            {t("books.title")}
          </h2>
        </div>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id, "/"))}
          className="p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD"
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>

      {/* Cards Grid */}
      <div className="w-full max-w-3xl px-4 md:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 items-stretch w-full">
          {/* Adjusting Journal Entry */}
          <Link
            to={companyRoute(company_id, "/journal-entries/adjusting")}
            className={cardBase}
          >
            <PencilSquareIcon className="w-7 h-7 mb-2 text-appleblue" />
            <Title>{t("books.journalEntry")}</Title>

            <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-amber-700">
              {t("books.advanced")}
            </div>

            <Subtitle>{t("books.adjustingEntries")}</Subtitle>
          </Link>

          {/* General Journal */}
          <Link to={companyRoute(company_id, "/general-journal")} className={cardBase}>
            <BookOpenIcon className="w-7 h-7 mb-2 text-appleblue" />
            <Title>{t("generalJournal.title")}</Title>
            <Subtitle>{t("books.viewAllTransactions")}</Subtitle>
          </Link>

          {/* General Ledger */}
          <Link to={companyRoute(company_id, "/general-ledger")} className={cardBase}>
            <Squares2X2Icon className="w-7 h-7 mb-2 text-appleblue" />
            <Title>{t("generalLedger.title")}</Title>
            <Subtitle>{t("books.accountRunningBalance")}</Subtitle>
          </Link>

          {/* Trial Balance */}
          <Link
            to={companyRoute(company_id, "/reports/trial-balance")}
            className={cardBase}
          >
            <ScaleIcon className="w-7 h-7 mb-2 text-appleblue" />
            <Title>{t("trialBalance.title")}</Title>
            <Subtitle>{t("books.allAccountsAtGlance")}</Subtitle>
          </Link>

          {/* Year-End Closing */}
          <Link
            to={companyRoute(company_id, "/settings/year-end")}
            className={cardBase}
          >
            <ShieldExclamationIcon className="w-7 h-7 mb-2 text-red-600" />
            <Title>{t("books.yearEndClosing")}</Title>

            <div className="mt-2 text-xs font-semibold uppercase tracking-wide text-red-600">
              {t("books.advanced")}
            </div>

            <div className="text-xs text-gray-500 mt-1 text-center">
              <span className="font-semibold text-red-600">{t("books.danger")}</span>{" "}{t("books.oneWayAction")}
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
